import { useState, useEffect, useCallback, useRef } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Clock, CheckCircle2, Flame, PackageCheck, UtensilsCrossed, Truck, XCircle, Wifi, WifiOff, ImageOff, Search } from 'lucide-react'
import toast from 'react-hot-toast'
import api, { storePaymentsAPI } from '../../services/api'
import { useSocket } from '../../contexts/SocketContext'
import { useCurrency, price } from './templates/shared'
import OrderPaymentPanel from '../../components/OrderPaymentPanel'
import { t } from '../../i18n'

const PAYMENT_LABELS = { cash: 'Cash', card: 'Card', bank_transfer: 'Bank transfer' }

function steps(order) {
  const last = order.order_type === 'DELIVERY' ? ['On its way / delivered', Truck] : order.order_type === 'DINE_IN' ? ['Served', UtensilsCrossed] : ['Collected', PackageCheck]
  return [
    ['PENDING', 'Order placed', Clock],
    ['CONFIRMED', 'Accepted', CheckCircle2],
    ['PREPARING', 'Being prepared', Flame],
    ['READY', 'Ready', PackageCheck],
    ['SERVED', last[0], last[1]],
  ]
}
const RANK = { PENDING: 0, AWAITING_PAYMENT: 0, CONFIRMED: 1, PREPARING: 2, READY: 3, SERVED: 4, COMPLETED: 5 }

// Customer order tracking (by PIN). Follows the storefront skin.
export default function OrderStatusPage() {
  const currency = useCurrency()
  const [searchParams] = useSearchParams()
  const pin = searchParams.get('pin')
  const [pinInput, setPinInput] = useState('')
  const [order, setOrder] = useState(null)
  const [loading, setLoading] = useState(!!pin)
  const [error, setError] = useState(null)
  const [failedImages, setFailedImages] = useState(new Set())
  const { socket, isConnected } = useSocket()
  const cardChecked = useRef(false)
  const query = new URLSearchParams(searchParams)
  query.delete('pin'); query.delete('paid')
  const backQuery = query.toString() ? `?${query}` : ''

  const load = useCallback(async (quiet = false) => {
    if (!pin) return
    if (!quiet) setLoading(true)
    try {
      const { data } = await api.get(`/api/orders/pin/${pin}`)
      setOrder(data.order)
      setError(null)
      return data.order
    } catch (err) {
      setError(t(err.response?.data?.error || 'We could not find this order.'))
    } finally {
      setLoading(false)
    }
  }, [pin])

  useEffect(() => { load() }, [load])

  // Back from Stripe (or reopening an unpaid card order): ask the server to
  // check with Stripe whether the payment went through.
  useEffect(() => {
    if (!order || cardChecked.current || order.payment_method !== 'card' || order.payment_status === 'PAID') return
    cardChecked.current = true
    storePaymentsAPI.confirmCard(pin).then(({ data }) => {
      if (data.paid) { toast.success(t("Payment received - thank you!")); load(true) }
      else if (searchParams.get('paid') === 'card') toast(t("Your payment is still processing. This page will update."), { icon: '⏳' })
    }).catch(() => {})
  }, [order, pin, load, searchParams])

  useEffect(() => {
    if (!socket || !order) return
    const onUpdate = updated => { if (updated?.id === order.id) load(true) }
    const onPayment = data => { if (data?.orderId === order.id) load(true) }
    socket.on('order.updated', onUpdate)
    socket.on('order.confirmed', onUpdate)
    socket.on('payment.recorded', onPayment)
    socket.on('order.payment_proof', onPayment)
    return () => {
      socket.off('order.updated', onUpdate)
      socket.off('order.confirmed', onUpdate)
      socket.off('payment.recorded', onPayment)
      socket.off('order.payment_proof', onPayment)
    }
  }, [socket, order, load])

  if (!pin) {
    return (
      <form className="sk-page sk-card max-w-md text-center" onSubmit={e => { e.preventDefault(); if (/^\d{8}$/.test(pinInput)) { const q = new URLSearchParams(searchParams); q.set('pin', pinInput); window.location.search = q.toString() } else toast.error(t("Enter the 8-digit PIN from your confirmation")) }}>
        <Search className="mx-auto sk-muted" size={36} aria-hidden="true" />
        <h1 className="sk-heading mt-3">{t("Track an order")}</h1>
        <p className="sk-muted mt-2">{t("Enter the 8-digit PIN from your order confirmation.")}</p>
        <label htmlFor="track-pin" className="sr-only">{t("Order PIN")}</label>
        <input id="track-pin" className="flow-input mt-6 text-center tracking-[0.3em] text-lg" inputMode="numeric" maxLength={8} value={pinInput} onChange={e => setPinInput(e.target.value.replace(/\D/g, ''))} placeholder="12345678" />
        <button className="sk-btn w-full mt-4">{t("Find my order")}</button>
      </form>
    )
  }

  if (loading) return <div className="sk-page text-center py-20 sk-muted" role="status">{t("Loading your order…")}</div>

  if (error || !order) {
    return (
      <section className="sk-page sk-card max-w-md text-center">
        <XCircle className="mx-auto text-red-500" size={40} aria-hidden="true" />
        <h1 className="sk-heading mt-3">{t("Order not found")}</h1>
        <p className="sk-muted mt-2">{error || t("No order matches PIN {pin}.", { pin: pin })}</p>
        <div className="flex gap-3 mt-6">
          <button onClick={() => load()} className="sk-btn flex-1">{t("Try again")}</button>
          <Link to={`/${backQuery}`} className="sk-btn-ghost flex-1">{t("Back to store")}</Link>
        </div>
      </section>
    )
  }

  const cancelled = order.status === 'CANCELLED'
  const rank = RANK[order.status] ?? 0
  const items = order.items || []
  const itemImage = item => {
    const url = item.image || item.menu_item?.image || item.menu_item_image
    if (!url) return null
    return url.startsWith('http') || url.startsWith('/') ? url : `/uploads/${url}`
  }

  return (
    <div className="sk-page max-w-3xl space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">{t("Order")} {order.order_code}</p>
          <h1 className="sk-heading">{cancelled ? t("Order cancelled") : order.status === 'COMPLETED' ? t("All done - thank you!") : rank >= 3 ? t("Your order is ready") : t("We’re on it")}</h1>
          <p className="sk-muted mt-2">{t("Tracking PIN")} <strong>{order.pin}</strong></p>
        </div>
        <span className="sk-chip">{isConnected ? <Wifi size={14} /> : <WifiOff size={14} />} {isConnected ? t("Live updates") : t("Connecting…")}</span>
      </div>

      <OrderPaymentPanel order={{ ...order, currency }} onUpdated={() => load(true)} />

      {!cancelled && (
        <section className="sk-card">
          <h2 className="text-lg font-semibold mb-5">{t("Progress")}</h2>
          <ol className="space-y-5">
            {steps(order).map(([key, label, Icon], i) => {
              const done = i < rank || order.status === 'COMPLETED'
              const active = i === rank && order.status !== 'COMPLETED'
              return (
                <li key={key} className="flex items-center gap-4" aria-current={active ? 'step' : undefined}>
                  <span className="w-11 h-11 rounded-full grid place-items-center flex-shrink-0 border-2 transition-colors"
                    style={done || active ? { background: 'var(--theme-primary)', borderColor: 'var(--theme-primary)', color: 'var(--sk-on-primary)' } : { borderColor: 'var(--sk-border)', color: 'var(--sk-muted)' }}>
                    <Icon size={19} aria-hidden="true" />
                  </span>
                  <div>
                    <p className={`font-semibold ${done || active ? '' : 'sk-muted'}`}>{t(label)}</p>
                    {active && <p className="text-sm sk-muted">{t("In progress")}</p>}
                  </div>
                </li>
              )
            })}
          </ol>
        </section>
      )}

      <section className="sk-card">
        <div className="flex flex-wrap justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold">{t("Your items")}</h2>
          <span className="sk-chip">{t(PAYMENT_LABELS[order.payment_method] || "Cash")} · {order.payment_status === 'PAID' ? t("Paid") : t("Not paid yet")}</span>
        </div>
        <ul className="divide-y divide-gray-100">
          {items.map((item, idx) => {
            const img = itemImage(item)
            const name = item.menu_item_name || item.name || `Item ${idx + 1}`
            const unit = Number(item.unit_price || item.price || 0)
            return (
              <li key={idx} className="flex gap-4 py-4 sk-divider">
                {img && !failedImages.has(idx)
                  ? <img src={img} alt="" className="sk-thumb !w-16 !h-16" onError={() => setFailedImages(prev => new Set(prev).add(idx))} />
                  : <div className="sk-thumb !w-16 !h-16 grid place-items-center"><ImageOff size={20} className="sk-muted" aria-hidden="true" /></div>}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{item.quantity} × {name}</p>
                  {item.variant_name && <p className="text-sm sk-muted">{item.variant_name}</p>}
                  {item.modifiers?.length > 0 && <p className="text-sm sk-muted">{item.modifiers.map(m => m.name).join(', ')}</p>}
                  {item.note && <p className="text-sm sk-muted italic">“{item.note}”</p>}
                </div>
                <span className="font-semibold whitespace-nowrap">{price(unit * (item.quantity || 1), currency)}</span>
              </li>
            )
          })}
        </ul>
        <div className="border-t sk-divider mt-2 pt-4 space-y-1 text-sm">
          {Number(order.tax) > 0 && <p className="flex justify-between sk-muted"><span>{t("Tax")}</span><span>{price(order.tax, currency)}</span></p>}
          {Number(order.service_charge) > 0 && <p className="flex justify-between sk-muted"><span>{t("Service charge")}</span><span>{price(order.service_charge, currency)}</span></p>}
          {Number(order.shipping_fee) > 0 && <p className="flex justify-between sk-muted"><span>{t("Delivery")}{order.shipping_area ? ` · ${order.shipping_area}` : ''}</span><span>{price(order.shipping_fee, currency)}</span></p>}
          <p className="flex justify-between text-lg font-semibold pt-2"><span>{t("Total")}</span><span>{price(order.total, currency)}</span></p>
        </div>
      </section>

      <Link to={`/${backQuery}`} className="sk-btn w-full">{t("Order more")}</Link>
    </div>
  )
}
